/**
 * API Service for AI Work Study
 * Centralized API client for backend communication
 */

import axios from 'axios';

// Get API URL from environment or use default
export const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';

/**
 * Survey API client class
 * Handles all communication with the backend API
 */
export class SurveyAPI {
    /**
     * Initializes API client with base URL
     * @param {string} baseURL - Base URL for the API (optional, uses env default)
     */
    constructor(baseURL = API_BASE_URL) {
        this.client = axios.create({
            baseURL: `${baseURL}/api`,
            headers: {
                'Content-Type': 'application/json',
            },
            timeout: 30000, // 30 second timeout for AI responses
        });

        // Add response interceptor for error handling
        this.client.interceptors.response.use(
            (response) => response,
            (error) => {
                console.error('API Error:', error.response?.data || error.message);
                return Promise.reject(error);
            }
        );
    }

    /**
     * Creates new survey session
     * @param {Object} sessionData - Employee information
     * @param {string} sessionData.employee_name - Employee's full name
     * @param {string} sessionData.role_title - Job title
     * @param {string} sessionData.department - Department name
     * @param {string} [sessionData.job_description] - Optional job description
     * @returns {Promise<Object>} Session with token and details
     */
    async startSurvey(sessionData) {
        try {
            const response = await this.client.post('/survey/start', sessionData);
            return response.data;
        } catch (error) {
            throw this._handleError(error, 'Failed to start survey');
        }
    }

    /**
     * Creates new survey session with job description file upload
     * @param {Object} sessionData - Employee information
     * @param {string} sessionData.employee_name - Employee's full name
     * @param {string} sessionData.role_title - Job title
     * @param {string} sessionData.department - Department name
     * @param {File} jobDescriptionFile - Job description file (PDF, DOCX, TXT)
     * @returns {Promise<Object>} Session with token and details
     */
    async startSurveyWithFile(sessionData, jobDescriptionFile) {
        try {
            const formData = new FormData();
            formData.append('employee_name', sessionData.employee_name);
            formData.append('role_title', sessionData.role_title);
            formData.append('department', sessionData.department);
            formData.append('job_description_file', jobDescriptionFile);

            const response = await this.client.post('/survey/start-with-file', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
                timeout: 60000, // 60 second timeout for file upload
            });
            return response.data;
        } catch (error) {
            throw this._handleError(error, 'Failed to start survey with file');
        }
    }

    /**
     * Gets next question for survey
     * @param {string} token - Survey session token
     * @returns {Promise<Object>} Next question data including:
     *   - question_number: number
     *   - question_text: string
     *   - is_followup: boolean
     *   - progress: object
     *   - is_complete: boolean
     */
    async getNextQuestion(token) {
        try {
            const response = await this.client.get(`/survey/${token}/next`);
            return response.data;
        } catch (error) {
            throw this._handleError(error, 'Failed to get next question');
        }
    }

    /**
     * Gets personalized welcome message based on job description
     * @param {string} token - Survey session token
     * @returns {Promise<Object>} Welcome message data including:
     *   - message: string (AI-generated personalized welcome)
     *   - employee_name: string
     *   - role_title: string
     *   - department: string
     */
    async getWelcomeMessage(token) {
        try {
            const response = await this.client.get(`/survey/${token}/welcome`);
            return response.data;
        } catch (error) {
            // If welcome message fails, return a fallback
            console.error('Failed to get personalized welcome:', error);
            return {
                message: null, // Will use default in frontend
                error: true
            };
        }
    }

/**
     * Submits an answer to a question
     * @param {string} token - Survey session token
     * @param {number} questionNumber - Question being answered
     * @param {string} answerText - User's answer
     * @param {string} questionText - The question text being answered
     * @returns {Promise<Object>} Next question or completion signal
     */
    async submitAnswer(token, questionNumber, answerText, questionText) {
        try {
            const response = await this.client.post(`/survey/${token}/answer`, {
                question_number: questionNumber,
                answer_text: answerText,
                question_text: questionText,
            });
            return response.data;
        } catch (error) {
            throw this._handleError(error, 'Failed to submit answer');
        }
    }

    /**
     * Gets current survey status and progress
     * @param {string} token - Survey session token
     * @returns {Promise<Object>} Status and progress information
     */
    async getSurveyStatus(token) {
        try {
            const response = await this.client.get(`/survey/${token}/status`);
            return response.data;
        } catch (error) {
            throw this._handleError(error, 'Failed to get survey status');
        }
    }

    /**
     * Gets all responses for a survey session
     * @param {string} token - Survey session token
     * @returns {Promise<Array>} List of question-answer pairs
     */
    async getSurveyResponses(token) {
        try {
            const response = await this.client.get(`/survey/${token}/responses`);
            return response.data;
        } catch (error) {
            throw this._handleError(error, 'Failed to get survey responses');
        }
    }

    /**
     * Updates an existing answer
     * @param {string} token - Survey session token
     * @param {number} questionNumber - Question number to update
     * @param {string} answerText - New answer text
     * @returns {Promise<Object>} Update result
     */
    async updateAnswer(token, questionNumber, answerText) {
        try {
            const response = await this.client.put(`/survey/${token}/answer`, {
                question_number: questionNumber,
                answer_text: answerText,
            });
            return response.data;
        } catch (error) {
            throw this._handleError(error, 'Failed to update answer');
        }
    }

    /**
     * Health check for API connectivity
     * @returns {Promise<Object>} Health status
     */
    async healthCheck() {
        try {
            const response = await axios.get(`${API_BASE_URL}/health`);
            return response.data;
        } catch (error) {
            throw this._handleError(error, 'API health check failed');
        }
    }

    /**
     * Confirms pre-filled survey details
     * @param {string} token - Survey session token
     * @returns {Promise<Object>} Confirmation status
     */
    async confirmDetails(token) {
        try {
            const response = await this.client.post(`/survey/${token}/confirm-details`);
            return response.data;
        } catch (error) {
            throw this._handleError(error, 'Failed to confirm details');
        }
    }

    /**
     * Uploads a document for AI context
     * @param {string} token - Survey session token
     * @param {File} file - File to upload
     * @returns {Promise<Object>} Document metadata
     */
    async uploadDocument(token, file) {
        try {
            const formData = new FormData();
            formData.append('file', file);
            
            const response = await this.client.post(`/documents/upload/${token}`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            return response.data;
        } catch (error) {
            throw this._handleError(error, 'Failed to upload document');
        }
    }

    /**
     * Lists all documents for a survey session
     * @param {string} token - Survey session token
     * @returns {Promise<Array>} List of documents
     */
    async listDocuments(token) {
        try {
            const response = await this.client.get(`/documents/${token}`);
            return response.data;
        } catch (error) {
            throw this._handleError(error, 'Failed to list documents');
        }
    }

    /**
     * Deletes a document
     * @param {string} token - Survey session token
     * @param {string} documentId - Document ID to delete
     * @returns {Promise<Object>} Deletion confirmation
     */
    async deleteDocument(token, documentId) {
        try {
            const response = await this.client.delete(`/documents/${token}/${documentId}`);
            return response.data;
        } catch (error) {
            throw this._handleError(error, 'Failed to delete document');
        }
    }

    /**
     * Updates employee details on a survey session (admin only)
     * @param {string} sessionId - Survey session ID
     * @param {Object} updates - Fields to update
     * @param {string} [updates.employee_name] - Updated name
     * @param {string} [updates.employee_email] - Updated email
     * @param {string} [updates.employee_id] - Updated employee ID
     * @param {string} [updates.role_title] - Updated role/title
     * @param {string} [updates.department] - Updated department
     * @returns {Promise<Object>} Update result
     */
    async updateSession(sessionId, updates) {
        try {
            const token = localStorage.getItem('adminToken');
            const swaPrincipal = localStorage.getItem('swaPrincipal');
            const response = await this.client.patch(`/admin/sessions/${sessionId}`, updates, {
                headers: {
                    'Content-Type': 'application/json',
                    ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
                    ...(swaPrincipal ? { 'X-MS-CLIENT-PRINCIPAL': swaPrincipal } : {}),
                }
            });
            return response.data;
        } catch (error) {
            throw this._handleError(error, 'Failed to update session');
        }
    }

    /**
     * Handles API errors and formats them consistently
     * @private
     */
    _handleError(error, defaultMessage) {
        if (error.response) {
            // Server responded with error
            const detail = error.response.data?.detail;
            return new Error(detail || defaultMessage);
        } else if (error.request) {
            // No response received
            return new Error('Unable to connect to server. Please check your connection.');
        } else {
            // Request setup error
            return new Error(error.message || defaultMessage);
        }
    }
}

// Export singleton instance for convenience
export const surveyAPI = new SurveyAPI();

// Helper functions for components
export const confirmDetails = (token) => surveyAPI.confirmDetails(token);
export const uploadDocument = (token, file) => surveyAPI.uploadDocument(token, file);
export const listDocuments = (token) => surveyAPI.listDocuments(token);
export const deleteDocument = (token, docId) => surveyAPI.deleteDocument(token, docId);

const getAdminHeaders = () => {
    const token = localStorage.getItem('adminToken');
    const swaPrincipal = localStorage.getItem('swaPrincipal');

    return {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        ...(swaPrincipal ? { 'X-MS-CLIENT-PRINCIPAL': swaPrincipal } : {}),
    };
};

const readApiError = async (response, fallback) => {
    const error = await response.json().catch(() => ({}));
    const detail = error.detail || error.message;

    if (typeof detail === 'string') return detail;
    if (detail?.message) return detail.message;
    if (detail?.error) return detail.error;
    return fallback;
};

export const getReportGenerationLock = async () => {
    const response = await fetch(`${API_BASE_URL}/api/admin/report-generation-lock`, {
        headers: getAdminHeaders(),
    });

    if (!response.ok) {
        throw new Error(await readApiError(response, 'Failed to load report generation lock status'));
    }

    return response.json();
};

export const lockReportGeneration = async (message = null) => {
    const response = await fetch(`${API_BASE_URL}/api/admin/report-generation-lock/lock`, {
        method: 'POST',
        headers: getAdminHeaders(),
        body: JSON.stringify({ message }),
    });

    if (!response.ok) {
        throw new Error(await readApiError(response, 'Failed to lock report generation'));
    }

    return response.json();
};

export const unlockReportGeneration = async () => {
    const response = await fetch(`${API_BASE_URL}/api/admin/report-generation-lock/unlock`, {
        method: 'POST',
        headers: getAdminHeaders(),
    });

    if (!response.ok) {
        throw new Error(await readApiError(response, 'Failed to unlock report generation'));
    }

    return response.json();
};

/**
 * Lock a department analysis to prevent accidental regeneration
 * @param {string} department - Department name
 * @param {string} reason - Optional reason for locking
 * @returns {Promise<Object>} Lock response with status
 */
export const lockDepartmentAnalysis = async (department, reason = null) => {
    const response = await fetch(
        `${API_BASE_URL}/api/admin/trends/department/${encodeURIComponent(department)}/lock`,
        {
            method: 'POST',
            headers: getAdminHeaders(),
            body: JSON.stringify({ reason })
        }
    );
    
    if (!response.ok) {
        throw new Error(await readApiError(response, 'Failed to lock department analysis'));
    }
    
    return response.json();
};

/**
 * Unlock a department analysis to allow regeneration
 * @param {string} department - Department name
 * @returns {Promise<Object>} Unlock response with status
 */
export const unlockDepartmentAnalysis = async (department) => {
    const response = await fetch(
        `${API_BASE_URL}/api/admin/trends/department/${encodeURIComponent(department)}/unlock`,
        {
            method: 'POST',
            headers: getAdminHeaders(),
        }
    );
    
    if (!response.ok) {
        throw new Error(await readApiError(response, 'Failed to unlock department analysis'));
    }
    
    return response.json();
};

export default surveyAPI;
